import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { THEME_COLORS } from '@shared/constants/theme-colors';

export const CustomTheme = definePreset(Aura, {
  scheme: 'dark',
  semantic: {
    primary: {
      50: '#F9FDEB',
      100: '#F0FBC8',
      200: '#E5F8A4',
      300: '#D4F071',
      400: '#C3E94C',
      500: '#BCEA42', // base color
      600: '#A5D537',
      700: '#88B52A',
      800: '#6B8F22',
      900: '#536E1B',
      950: '#0E231C', // deep tone
    },
    colorScheme: {
      light: {
        overlay: {
          popover: {
            background: '#0E231C',
            color: '{primary.50}',
          },
        },
        formField: {
          background: '{surface.0}',
          hoverBorderColor: '{surface.300}',
          placeholderColor: '{surface.300}',
          color: '{primary.50}',
          borderColor: '{gray.300}',
          iconColor: '{gray.300}',
          focusBorderColor: '{primary.500}',
          disabledColor: '{gray.400}',
          disabledBackground: '{dark.surface.0}',
          invalidBorderColor: '#FF5449',
          invalidPlaceholderColor: '#FF5449',
        },
        surface: {
          0: '#0E231C',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}',
        },
      },
      dark: {
        overlay: {
          popover: {
            background: '#0E231C',
            color: '{primary.50}',
          },
          modal: {
            background: '#0E231C',
            color: '{primary.50}',
          }
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '{primary.950}',
          hoverColor: '{primary.700}',
          activeColor: '{primary.500}',
        },
        formField: {
          background: 'transparent',
          hoverBorderColor: THEME_COLORS.schemes.onSurfaceVariant,
          placeholderColor: THEME_COLORS.schemes.onSurfaceVariant,
          color: THEME_COLORS.schemes.secondary,
          borderColor: THEME_COLORS.schemes.onSurfaceVariant,
          iconColor: THEME_COLORS.schemes.onSurfaceVariant,
          focusBorderColor: '{primary.500}',
          disabledColor: THEME_COLORS.schemes.onSurfaceVariant,
          disabledBackground: 'transparent',
          invalidBorderColor: THEME_COLORS.error,
          invalidPlaceholderColor: THEME_COLORS.error,
        },
        content: {
          background: '#0E231C',
          hoverBackground: '{dark.surface.500}',
          borderColor: '#1E3E31',
          color: '{primary.50}',
          hoverColor: '{primary.500}',
        },
        surface: {
          0: '#0B1A15', // casi negro — fondo global
          50: '#0E231C', // base original
          100: '#123228',
          200: '#164033',
          300: '#1B4E3E',
          400: '#215D49',
          500: '#2A6B55',
          600: '#347A61',
          700: '#3E8A6E',
          800: '#499B7A',
          900: '#54AD87',
          950: '#60BF94', // tono más claro para bordes o elevaciones
        },
      },
    },
  },
  components: {
    select: {
      overlay: {
        background: '#0E231C',
        borderColor: '#0E231C',
        color: '#FFFFFF',
      },
      option: {
        color: '#FFFFFF',
        focusColor: '#FFFFFF',
        focusBackground: 'rgba(188, 234, 66, 0.12)',
        selectedColor: '#FFFFFF',
        selectedFocusColor: '#FFFFFF',
        selectedFocusBackground: 'rgba(188, 234, 66, 0.12)',
      },
    },
    /** Panel e ítems: verde militar / superficie, sin foco “lima” del Aura por defecto */
    autocomplete: {
      overlay: {
        background: '#0E231C',
        borderColor: '#1E3E31',
        color: '#B5CCBB',
      },
      list: {
        gap: '0.25rem',
        padding: '0.5rem',
      },
      option: {
        color: '#BFBFBF',
        focusColor: '#EDEDED',
        focusBackground: 'rgba(27, 78, 62, 0.55)',
        selectedColor: '#EDEDED',
        selectedBackground: 'rgba(33, 93, 73, 0.45)',
        selectedFocusColor: '#EDEDED',
        selectedFocusBackground: 'rgba(42, 107, 85, 0.5)',
      },
      dropdown: {
        background: '#123228',
        borderColor: '#1E3E31',
        color: '#BFBFBF',
        hoverBackground: '#164033',
        hoverBorderColor: '#1E3E31',
        hoverColor: '#EDEDED',
        activeBackground: '#1B4E3E',
        activeBorderColor: '#1E3E31',
        activeColor: '#EDEDED',
      },
    },
    dialog: {
      root: {
        background: '#1d332f',
        borderColor: '{primary.500}',
        borderRadius: '12px',
        shadow: 'none',
      },
    },
    toast: {
      root: {
        borderRadius: '12px',
        borderWidth: '0',
      },
      success: {
        background: '#31443E', // --Schemes-Surface-Container
        borderColor: 'transparent',
        color: '#EDEDED', // --Schemes-On-Surface
        detailColor: '#BFBFBF', // --Schemes-On-Surface-Variant
        shadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        closeButton: {
          hoverBackground: 'rgba(255, 255, 255, 0.1)',
        },
      },
      error: {
        background: '#31443E', // --Schemes-Surface-Container
        borderColor: 'transparent',
        color: '#EDEDED', // --Schemes-On-Surface
        detailColor: '#BFBFBF', // --Schemes-On-Surface-Variant
        shadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        closeButton: {
          hoverBackground: 'rgba(255, 255, 255, 0.1)',
        },
      },
      info: {
        background: '#31443E', // --Schemes-Surface-Container
        borderColor: 'transparent',
        color: '#EDEDED', // --Schemes-On-Surface
        detailColor: '#BFBFBF', // --Schemes-On-Surface-Variant
        shadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        closeButton: {
          hoverBackground: 'rgba(255, 255, 255, 0.1)',
        },
      },
      warn: {
        background: '#31443E', // --Schemes-Surface-Container
        borderColor: 'transparent',
        color: '#EDEDED', // --Schemes-On-Surface
        detailColor: '#BFBFBF', // --Schemes-On-Surface-Variant
        shadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        closeButton: {
          hoverBackground: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});
